<template>
  <div class="app-navbar-actions">

    <!-- ── Notificações ── -->
    <VaDropdown v-model="notifOpen" class="app-navbar-actions__item" placement="bottom" :offset="[0, 12]" stick-to-edges>
      <template #anchor>
        <button class="action-btn" aria-label="Notificações" @click="handleNotificationClick">
          <span class="material-icons action-btn__icon">notifications_active</span>
          <span v-if="unreadCount > 0" class="action-btn__badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
        </button>
      </template>

      <div class="notification-dropdown">
        <div v-if="notifications.length === 0" class="notification-dropdown__empty">
          Nenhuma notificação
        </div>

        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ 'notification-item--read': notification.read }"
          @click.stop.prevent="openNotification(notification)"
        >
          <div class="notification-item__body">
            <div class="notification-item__title">{{ notification.title }}</div>
            <div class="notification-item__text">{{ notification.body }}</div>
          </div>
          <span v-if="!notification.read" class="notification-item__dot" aria-label="Não lida" />
        </div>

        <div v-if="unreadCount > 0" class="notification-dropdown__footer">
          <VaButton size="small" preset="primary" @click.stop.prevent="markAllAsRead">
            Marcar todas como lidas
          </VaButton>
        </div>

        <div v-if="notifications.length > 0" class="notification-dropdown__pagination">
          <VaButton
            size="small"
            preset="secondary"
            :disabled="currentPage <= 1"
            @click.stop.prevent="fetchNotifications(currentPage - 1)"
          >Anterior</VaButton>
          <span class="notification-dropdown__page">Página {{ currentPage }} de {{ totalPages }}</span>
          <VaButton
            size="small"
            preset="secondary"
            :disabled="currentPage >= totalPages"
            @click.stop.prevent="fetchNotifications(currentPage + 1)"
          >Próxima</VaButton>
        </div>
      </div>
    </VaDropdown>

    <!-- ── Theme toggle pill ── -->
    <div class="app-navbar-actions__item">
      <button
        class="theme-pill"
        :class="isDarkMode ? 'theme-pill--dark' : 'theme-pill--light'"
        role="switch"
        :aria-checked="isDarkMode"
        aria-label="Alternar tema"
        @click="isDarkMode = !isDarkMode"
      >
        <span class="theme-pill__knob">
          <span class="material-icons theme-pill__icon">{{ isDarkMode ? 'bedtime' : 'sunny' }}</span>
        </span>
      </button>
    </div>

    <!-- ── User chip com dropdown ── -->
    <VaDropdown class="app-navbar-actions__item" placement="bottom-end" :offset="[0, 8]" stick-to-edges>
      <template #anchor>
        <button class="user-chip" :title="store.name">
          <img
            :src="store.profile || '/profile.png'"
            :alt="store.name"
            class="user-chip__avatar"
          />
          <span class="user-chip__name">{{ store.name }}</span>
        </button>
      </template>

      <div class="profile-menu">
        <button class="profile-menu__item" @click="goToPreferences">
          <span style="color: #2D2E2E !important;" class="material-icons profile-menu__icon">manage_accounts</span>
          Preferências
        </button>
        <!-- Só quando o navegador oferece a instalação (Chrome/Edge, app ainda não instalado). -->
        <button v-if="canInstall" class="profile-menu__item" @click="promptInstall">
          <span style="color: #2D2E2E !important;" class="material-icons profile-menu__icon">install_desktop</span>
          {{ t('user.install_app') }}
        </button>
        <div class="profile-menu__sep" />
        <button class="profile-menu__item profile-menu__item--danger" @click="handleLogout">
          <span class="material-icons profile-menu__icon">logout</span>
          Sair
        </button>
      </div>
    </VaDropdown>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '../../../stores/user-store'
import { usePwaInstall } from '@/composables/usePwaInstall'
import axios from '@/services/api/api'
import { showToastSuccess, showToastError, showToastWarning } from '@/utils/toast1.js'

const store = useUserStore()
const router = useRouter()
const { t } = useI18n()
const { canInstall, promptInstall } = usePwaInstall()

const goToPreferences = () => router.push('/preferences')

const handleLogout = async () => {
  // revoga a sessão no servidor (rotaciona o jti → invalida access e refresh)
  try {
    await axios.delete('users/sign_out')
  } catch (_) { /* segue com o logout local */ }
  localStorage.removeItem('authToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('app_theme')
  window.location.href = '/auth/login'
}

function applyThemeFromStore() {
  const t = String(store.theme || '').toLowerCase()
  const isDark = t === 'dark'
  if (typeof window !== 'undefined' && (window as any).setAppTheme) {
    ;(window as any).setAppTheme(isDark ? 'dark' : 'light')
  }
}

const isDarkMode = computed({
  get() { return String(store.theme).toLowerCase() === 'dark' },
  set(val: boolean) {
    const theme = val ? 'dark' : 'light'
    if (typeof window !== 'undefined' && (window as any).setAppTheme) {
      ;(window as any).setAppTheme(theme)
      try { window.dispatchEvent(new Event('app-theme-changed')) } catch (e) { /* ignore */ }
    }
    axios.put('/users/' + store.id, { theme: val ? 1 : 0 }).catch(() => {})
    try { store.theme = theme } catch (e) { /* ignore */ }
  },
})

watch(() => store.theme, () => applyThemeFromStore(), { immediate: true })

// Notifications
const notifications = ref([])
const unreadCount = ref(0)
const currentPage = ref(1)
const totalPages = ref(1)
const perPage = 10
const notifOpen = ref(false)

const handleNotificationClick = () => fetchNotifications(1)

const fetchNotifications = async (page = 1) => {
  try {
    const res = await axios.get('/notifications', { params: { page, per_page: perPage } })
    notifications.value = res.data.notifications
    unreadCount.value = res.data.notifications.filter((n: any) => !n.read).length
    currentPage.value = res.data.meta?.current_page
    totalPages.value = res.data.meta?.total_pages
  } catch (err) {
    console.error('Erro ao buscar notificações', err)
  }
}

const markAllAsRead = async () => {
  if (unreadCount.value === 0) {
    showToastWarning('Todas as notificações já estão lidas.')
    return
  }
  try {
    await axios.post('/notifications/mark_all_as_read')
    await fetchNotifications(currentPage.value)
    unreadCount.value = 0
    showToastSuccess('Todas as notificações foram marcadas como lidas.')
  } catch (err) {
    showToastError('Erro ao marcar todas como lidas.')
  }
}

const markAsRead = async (notification: any) => {
  if (notification.read) return
  try {
    await axios.post(`/notifications/${notification.id}/mark_as_read`)
    notification.read = true
    if (unreadCount.value > 0) unreadCount.value -= 1
  } catch (err) {
    console.error('Erro ao marcar notificação como lida', err)
  }
}

// Clica numa notificação: marca como lida e, se for de ticket, abre o ticket certo.
const openNotification = async (notification: any) => {
  await markAsRead(notification)
  if (notification.subject_type === 'Ticket' && notification.subject_id) {
    notifOpen.value = false
    router.push({ name: 'sd-ticket', params: { id: notification.subject_id } })
  } else if (notification.subject_type === 'ProjectTask' && notification.subject_id) {
    notifOpen.value = false
    try {
      const res = await axios.get(`/project_tasks/${notification.subject_id}`)
      const task = res.data.project_task
      if (task) router.push({ name: 'project-projeto', params: { id: task.project_id }, query: { task: task.code } })
    } catch (e) {
      /* tarefa removida */
    }
  } else if (notification.subject_type === 'Project' && notification.subject_id) {
    notifOpen.value = false
    router.push({ name: 'project-projeto', params: { id: notification.subject_id } })
  } else if (notification.subject_type === 'SupportOnCallShift') {
    // Troca/cobertura/cessão de plantão: abre a escala.
    notifOpen.value = false
    router.push({ name: 'sd-escala' })
  }
}

let pollTimer: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  fetchNotifications(1)
  // Atualização automática (poll-based, sem ActionCable) para eventos de ticket aparecerem.
  pollTimer = setInterval(() => fetchNotifications(1), 60000)
})
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<style lang="scss">
// ── Wrapper das ações ────────────────────────────────────────
.app-navbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;

  &__item {
    display: flex;
    align-items: center;
  }
}

// ── Botão de ação genérico (notificação, etc.) ───────────────
.action-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: var(--shell-text, #15171d);
  transition: background 0.15s;
  padding: 0;

  &:hover { background: var(--shell-item-hover, rgba(0,0,0,0.05)); }
  &:focus-visible { outline: 2px solid #ff4d2e; outline-offset: 2px; }

  &__icon { font-size: 20px; }

  &__badge {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 16px;
    height: 16px;
    padding: 0 3px;
    border-radius: 8px;
    background: #ff4d2e;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
}

// ── Notification dropdown ────────────────────────────────────
.notification-dropdown {
  width: 26rem;
  max-height: 30rem;
  overflow-y: auto;
  background: var(--shell-glass-bg, #fff);
  border: 1px solid var(--shell-glass-border, rgba(0,0,0,0.08));
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  backdrop-filter: blur(20px);

  &__empty {
    padding: 1.5rem 1rem;
    text-align: center;
    font-size: 14px;
    color: var(--shell-text-muted, rgba(21,23,29,0.45));
  }

  &__footer,
  &__pagination {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid var(--shell-glass-border, rgba(0,0,0,0.08));
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  &__pagination { justify-content: space-between; }

  &__page {
    font-size: 12px;
    color: var(--shell-text-muted);
  }
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--shell-glass-border, rgba(0,0,0,0.06));
  transition: background 0.15s;

  &:hover { background: var(--shell-item-hover, rgba(0,0,0,0.03)); }
  &--read { opacity: 0.6; }

  &__body { flex: 1; min-width: 0; }

  &__title {
    font-size: 13px;
    font-weight: 500;
    color: var(--shell-text, #15171d);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    word-break: break-word;
  }

  &__text {
    font-size: 12px;
    color: var(--shell-text-muted, rgba(21,23,29,0.55));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ff4d2e;
    flex-shrink: 0;
    margin-top: 4px;
  }
}

// ── Theme pill toggle ────────────────────────────────────────
.theme-pill {
  width: 56px;
  height: 30px;
  border-radius: 15px;
  border: none;
  cursor: pointer;
  position: relative;
  padding: 0;
  transition: background 0.2s;
  flex-shrink: 0;

  &--light {
    background: rgba(0, 0, 0, 0.12);

    .theme-pill__knob {
      left: 3px;
      background: #fff;
    }
  }

  &--dark {
    background: #ff4d2e;

    .theme-pill__knob {
      left: calc(100% - 25px);
      background: #fff;
    }

    .theme-pill__icon {
      color: #1C1C1C !important;
    }
  }

  &--light {
    .theme-pill__icon {
      color: #f59e0b !important;
    }
  }

  &:focus-visible { outline: 2px solid #ff4d2e; outline-offset: 2px; }

  &__knob {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: left 0.2s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }

  &__icon {
    font-size: 13px;
    line-height: 1;
    color: rgba(0, 0, 0, 0.55) !important;
  }
}

// ── User chip ────────────────────────────────────────────────
.user-chip {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 3px 10px 3px 3px;
  border-radius: 20px;
  background: var(--shell-item-hover, rgba(0,0,0,0.04));
  border: none;
  cursor: pointer;
  color: var(--shell-text, #15171d);
  font-size: 13.5px;
  font-weight: 500;
  font-family: inherit;
  transition: background 0.15s;
  max-width: 160px;

  &:hover { background: var(--shell-glass-border, rgba(0,0,0,0.08)); }
  &:focus-visible { outline: 2px solid #ff4d2e; outline-offset: 2px; }

  &__avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// ── Profile dropdown menu ─────────────────────────────────────
.profile-menu {
  min-width: 180px;
  background: var(--shell-glass-bg, #fff);
  border: 1px solid var(--shell-glass-border, rgba(0,0,0,0.08));
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  backdrop-filter: blur(20px);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 8px;
    background: none;
    cursor: pointer;
    font-size: 13.5px;
    font-family: inherit;
    font-weight: 500;
    color: var(--shell-text, #15171d);
    text-align: left;
    transition: background 0.15s;

    &:hover { background: var(--shell-item-hover, rgba(0,0,0,0.05)); }

    &--danger {
      color: #e53935;
      &:hover { background: rgba(229, 57, 53, 0.08); }
    }
  }

  &__icon {
    font-size: 18px;
    flex-shrink: 0;
    color: rgba(21, 23, 29, 0.6) !important;
  }

  &__item--danger &__icon {
    color: #e53935 !important;
  }

  &__sep {
    height: 1px;
    background: var(--shell-glass-border, rgba(0,0,0,0.08));
    margin: 2px 0;
  }
}
</style>
